/**
 * Engine Atlas title block substrate fetch + composition. Per build prompt
 * Phase 3 + Sol D-locks 2026-05-27 (docs/atlas-day-1-sol-locks.md).
 *
 * fetchTitleBlockSubstrate: 5 parallel PostgREST queries returning the
 * substrate inputs the title block prose interpolates:
 *   - params (3 keys: min_roi_pct, min_margin_pct, days_to_sell_default)
 *   - buy_score_weight_components active labels in sort_order (D2: enumerate
 *     all 7 verbatim)
 *   - active distributors primary row (D3: full name "Brooklyn Low Voltage
 *     Supply" + payment_terms integer; composition layer formats to "net-N")
 *   - active marketplaces ordered by display_name alphabetical (D4:
 *     marketplaces.sort_order column absent → display_name fallback; BACKLOG
 *     candidate for Atlas Day 3 close-out sweep)
 *   - brands count where enforces_map=true (D5: substrate state today = 0)
 *
 * composeTitleBlock: clause-by-clause conditional composition (D5 architecture
 * lock). Each sentence built from clauses; MAP clause varies by
 * mapEnforcedBrandCount; pattern extends to future substrate-state-conditional
 * clauses without restructure.
 *
 * Three sentences per scope doc §5 line 234 intent — buying criteria + ranking
 * / operational posture (who, where, MAP) / forward-looking posture (restock).
 *
 * Substrate dependency: queries Supabase via arbi-dev's own supabase client
 * (no cross-app cache sharing with arbi-root useSettingsConfig per Phase 0
 * item 12 disposition — keeps cache layer simple). No caching at Day 1; direct
 * fetch on TitleBlock mount. If Day 2 needs caching, lift to React Query.
 *
 * D1: days_to_sell_default = 30 is the substrate source for the "holding period"
 *     wording in surface. Param key itself surfaces in Atlas Day 2 inspect trace.
 */

import { supabase } from '../supabase';

// ── Types ────────────────────────────────────────────────────────────────────

interface PrimaryDistributor {
  name: string;
  paymentTerms: number;
}

export interface TitleBlockSubstrate {
  minRoiPct: number;
  minMarginPct: number;
  daysToSellDefault: number;
  buyScoreLabels: string[];
  primaryDistributor: PrimaryDistributor | null;
  marketplaces: string[];
  mapEnforcedBrandCount: number;
}

export interface TitleBlockSentences {
  sentence1: string;
  sentence2: string;
  sentence3: string;
}

// ── Substrate fetch ─────────────────────────────────────────────────────────

export async function fetchTitleBlockSubstrate(): Promise<TitleBlockSubstrate> {
  const [paramsRes, bswcRes, distRes, mkRes, brRes] = await Promise.all([
    supabase
      .from('params')
      .select('key, value')
      .eq('level', 'business')
      .is('entity_id', null)
      .in('key', ['min_roi_pct', 'min_margin_pct', 'days_to_sell_default']),
    supabase
      .from('buy_score_weight_components')
      .select('label, sort_order')
      .eq('is_available', true)
      .order('sort_order', { ascending: true }),
    supabase
      .from('distributors')
      .select('name, payment_terms')
      .eq('is_active', true)
      .order('name', { ascending: true })
      .limit(1)
      .maybeSingle(),
    supabase
      .from('marketplaces')
      .select('display_name')
      .eq('is_active', true)
      .order('display_name', { ascending: true }),
    supabase
      .from('brands')
      .select('id', { count: 'exact', head: true })
      .eq('enforces_map', true),
  ]);

  const params = (paramsRes.data ?? []) as Array<{ key: string; value: unknown }>;
  const readParam = (key: string): number => {
    const row = params.find((p) => p.key === key);
    if (!row) return 0;
    return typeof row.value === 'number' ? row.value : Number(row.value);
  };

  const distRow = distRes.data as { name: string; payment_terms: number } | null;
  const primaryDistributor: PrimaryDistributor | null = distRow
    ? { name: distRow.name, paymentTerms: distRow.payment_terms }
    : null;

  const bswcRows = (bswcRes.data ?? []) as Array<{ label: string; sort_order: number }>;
  const mkRows = (mkRes.data ?? []) as Array<{ display_name: string }>;

  return {
    minRoiPct: readParam('min_roi_pct'),
    minMarginPct: readParam('min_margin_pct'),
    daysToSellDefault: readParam('days_to_sell_default'),
    buyScoreLabels: bswcRows.map((r) => r.label),
    primaryDistributor,
    marketplaces: mkRows.map((r) => r.display_name),
    mapEnforcedBrandCount: brRes.count ?? 0,
  };
}

// ── Natural-language list joiner ─────────────────────────────────────────────

/** @public — root-tests-import-apps (U11 census C1): consumed by the root suite (atlasDay1Foundation.test.ts). */
export function joinList(items: string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

// ── Composition (clause-by-clause conditional per D5) ────────────────────────

export function composeTitleBlock(substrate: TitleBlockSubstrate): TitleBlockSentences {
  // Sentence 1 — buying criteria + ranking. D2: enumerate all active labels.
  const sentence1 =
    `ISG currently considers a product worth buying when ROI exceeds ${substrate.minRoiPct}%, ` +
    `margin clears ${substrate.minMarginPct}%, and velocity supports a ${substrate.daysToSellDefault}-day holding period — ` +
    `ranked by a Buy Score blending ${joinList(substrate.buyScoreLabels)}.`;

  // Sentence 2 — operational posture + conditional MAP clause.
  //   D3: full distributor name + integer→"net-N" template.
  //   D4: marketplace display_names alphabetical, no FBA/FBM split.
  //   D5: clause-by-clause variant — MAP-present vs MAP-absent.
  const distName = substrate.primaryDistributor?.name ?? 'no active distributor';
  const distTerms = substrate.primaryDistributor?.paymentTerms ?? 0;
  const operationalLead = `ISG buys primarily through ${distName} on net-${distTerms} terms across ${joinList(substrate.marketplaces)}`;
  const mapClause =
    substrate.mapEnforcedBrandCount > 0
      ? `; enforces MAP on ${substrate.mapEnforcedBrandCount} ${substrate.mapEnforcedBrandCount === 1 ? 'brand' : 'brands'} as flag-and-report.`
      : '; has not yet configured MAP enforcement.';
  const sentence2 = `${operationalLead}${mapClause}`;

  // Sentence 3 — forward-looking posture. Locked phrasing per scope doc line 232
  // (structural stance over axis-label editorializing).
  const sentence3 = 'It flags inventory for restock tiered by projected time-to-stockout.';

  return { sentence1, sentence2, sentence3 };
}
